import { useContext } from 'react';
import { FilledButton, ImageCropper, type ImageCrop } from 'components';
import { cropDataURL } from 'utils/image';
import { POSTCARD_IMAGE_SIZE } from '../Front';
import { postcardContext } from 'contexts/Communicate/Postcard/PostcardContext';
import { Fade, Grid } from '@mui/material';
import {
  S3UploadOutputType,
  UploadFileToS3,
} from 'hooks/useUploadFileToS3';
import { StateSetter } from 'types/utils/React';
import { FormattedMessage } from 'react-intl';
import PostcardPlaceholderImage from 'assets/images/postcard/postcard_placeholder.png';
import { trackEvent, GACommonEventAction, GAEventCategory} from 'services/googleAnalytics';

type PostcardImageCropPureProps = {
  crop: ImageCrop;
  imagePreview: string;
  showFront: boolean;
  handleCancel: Function;
  handleCrop: Function;
  setCrop: StateSetter<ImageCrop>;
};

export const PostcardImageCropPure = ({
  crop,
  imagePreview,
  showFront,
  handleCancel,
  handleCrop,
  setCrop,
}: PostcardImageCropPureProps): JSX.Element => (
  <>
    <ImageCropper crop={crop} src={imagePreview} setCrop={setCrop} />

    <Fade in={showFront} timeout={1000}>
      <Grid container justifyContent="flex-end" spacing={2}>
        <Grid item>
          <FilledButton
            color="green"
            data-testid="crop-button"
            onClick={(): void => handleCrop()}
          >
            <FormattedMessage id="postcard.crop" />
          </FilledButton>
        </Grid>

        <Grid item>
          <FilledButton
            color="light_red"
            data-testid="cancel-crop-button"
            onClick={(): void => handleCancel()}
          >
            <FormattedMessage id="button.cancel" />
          </FilledButton>
        </Grid>
      </Grid>
    </Fade>
  </>
);

type PostcardImageCropProps = {
  crop: ImageCrop;
  showCrop: boolean;
  setCrop: StateSetter<ImageCrop>;
  setShowCrop: StateSetter<boolean>;
  uploadFileToS3: UploadFileToS3;
};

const PostcardImageCrop = ({
  crop,
  showCrop,
  setCrop,
  setShowCrop,
  uploadFileToS3,
}: PostcardImageCropProps): JSX.Element | null => {
  const { imagePreview, showFront, setImagePreview, setS3UploadedImage } =
    useContext(postcardContext);

  if (!showCrop) {
    return null;
  }

  const handleCrop = async (): Promise<void> => {
    trackEvent(
      GAEventCategory.POSTCARD,
      GACommonEventAction.CLICK,
      "Recadrer l'image",
    );

    const newDataUrl = await cropDataURL({
      crop,
      dataURL: imagePreview,
      targetHeight: POSTCARD_IMAGE_SIZE.HEIGHT,
      targetWidth: POSTCARD_IMAGE_SIZE.WIDTH,
    });

    const res: S3UploadOutputType = await uploadFileToS3(
      newDataUrl,
      'image/jpeg',
    );

    setImagePreview(res.dataURL);
    setS3UploadedImage(res);
    setShowCrop(false);
  };

  const handleCancel = () => {
    trackEvent(
      GAEventCategory.POSTCARD,
      GACommonEventAction.CLICK,
      "Annuler le recadrage de l'image",
    );

    setShowCrop(false);
    setImagePreview(PostcardPlaceholderImage as string);
    setS3UploadedImage(undefined);
  };

  return (
    <PostcardImageCropPure
      crop={crop}
      imagePreview={imagePreview}
      showFront={showFront}
      handleCancel={handleCancel}
      handleCrop={handleCrop}
      setCrop={setCrop}
    />
  );
};

export default PostcardImageCrop;
